import React, { useMemo } from 'react';
import { ClockIcon, TruckIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { formatDateForDisplay, isValidDate } from '../../constants/dates';
import type { Case } from '../../types';

/**
 * 時間文字列から表示用の時間フォーマット（HH:mm）を安全に抽出する
 * @param timeString - 時間文字列 ("08:00:00" や "08:00" など)
 * @returns 表示用時間文字列 ("08:00" または "--:--")
 */
const extractDisplayTime = (timeString: string | null | undefined): string => {
  if (!timeString || typeof timeString !== 'string') {
    return '--:--';
  }

  // 最低5文字以上（HH:mm）のフォーマットが必要
  if (timeString.length < 5) {
    return timeString;
  }

  // 時間フォーマットの基本的な妥当性をチェック
  if (!/^[0-9]{2}:[0-9]{2}/.test(timeString)) {
    return '--:--';
  }

  return timeString.substring(0, 5); // "08:00:00" → "08:00"
};

interface DriverTimeSlotViewProps {
  cases: Case[];
  currentTime?: string;
}

interface TimeSlot {
  time: string;
  hour: number;
  minute: number;
  displayTime: string;
}

interface CaseEvent {
  id: string;
  caseId: string;
  type: 'pickup' | 'delivery';
  time: string;
  vehiclePlateNumber: string;
  partnerName: string;
  address: string;
  status: string;
  duration: number; // minutes
}

/**
 * 陸送員専用タイムスロット表示コンポーネント
 * 縦長のタイムライン形式で今日の案件スケジュールを表示
 */
export const DriverTimeSlotView: React.FC<DriverTimeSlotViewProps> = ({ cases, currentTime }) => {
  // 表示する日付を取得（VITE_FIXED_DATEまたは今日の日付）
  const displayDate = useMemo(() => {
    const fixedDate = import.meta.env.VITE_FIXED_DATE;
    return fixedDate && isValidDate(fixedDate) ? fixedDate : new Date().toISOString().split('T')[0];
  }, []);
  // 6:00-20:00の30分単位でタイムスロットを生成
  const timeSlots = useMemo((): TimeSlot[] => {
    const slots: TimeSlot[] = [];
    for (let hour = 6; hour <= 20; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push({
          time,
          hour,
          minute,
          displayTime: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`,
        });
      }
    }
    return slots;
  }, []);

  // 案件から時間イベントを抽出
  const caseEvents = useMemo((): CaseEvent[] => {
    const events: CaseEvent[] = [];

    cases.forEach(caseItem => {
      // 引き取りイベント
      if (caseItem.pickupTimeStart) {
        events.push({
          id: `${caseItem.id}-pickup`,
          caseId: caseItem.id,
          type: 'pickup',
          time: extractDisplayTime(caseItem.pickupTimeStart), // "08:00:00" → "08:00"
          vehiclePlateNumber: caseItem.vehiclePlateNumber || '車両番号未設定',
          partnerName: caseItem.pickupPartnerId || '取引先未設定',
          address: caseItem.pickupAddress || '住所未設定',
          status: caseItem.status,
          duration: 30, // 引き取り作業時間（仮）
        });
      }

      // 搬入イベント
      if (caseItem.deliveryTime) {
        events.push({
          id: `${caseItem.id}-delivery`,
          caseId: caseItem.id,
          type: 'delivery',
          time: extractDisplayTime(caseItem.deliveryTime), // "11:30:00" → "11:30"
          vehiclePlateNumber: caseItem.vehiclePlateNumber || '車両番号未設定',
          partnerName: caseItem.deliveryPartnerId || '取引先未設定',
          address: caseItem.deliveryAddress || '住所未設定',
          status: caseItem.status,
          duration: 30, // 搬入作業時間（仮）
        });
      }
    });

    return events.sort((a, b) => a.time.localeCompare(b.time));
  }, [cases]);

  // 現在時刻のインデックスを取得
  const currentTimeSlotIndex = useMemo(() => {
    if (!currentTime) return -1;
    const currentHour = parseInt(currentTime.split(':')[0]);
    const currentMinute = parseInt(currentTime.split(':')[1]);
    return timeSlots.findIndex(
      slot => slot.hour === currentHour && Math.abs(slot.minute - currentMinute) < 30
    );
  }, [currentTime, timeSlots]);

  // 特定時間スロットのイベントを取得（完全一致優先 + 30分範囲フォールバック）
  const getEventsForTime = (slotTime: string): CaseEvent[] => {
    // 完全一致を最優先
    const exactMatches = caseEvents.filter(event => event.time === slotTime);
    if (exactMatches.length > 0) {
      return exactMatches;
    }

    // 30分スロット範囲内のイベントをチェック
    const [slotHour, slotMinute] = slotTime.split(':').map(Number);
    const slotMinutes = slotHour * 60 + slotMinute;

    return caseEvents.filter(event => {
      const [eventHour, eventMinute] = event.time.split(':').map(Number);
      const eventMinutes = eventHour * 60 + eventMinute;

      // イベントがこの30分スロット内にあるかチェック
      // 08:00スロットなら08:00-08:29、08:30スロットなら08:30-08:59
      return eventMinutes >= slotMinutes && eventMinutes < slotMinutes + 30;
    });
  };

  // ケースの状態に応じたスタイル
  const getCaseEventStyle = (event: CaseEvent) => {
    const baseStyle = 'p-3 rounded-lg border-l-4 mb-2';
    const statusColors = {
      assigned: 'bg-blue-50 dark:bg-blue-900/20 border-l-blue-500',
      in_transit: 'bg-yellow-50 dark:bg-yellow-900/20 border-l-yellow-500',
      completed: 'bg-green-50 dark:bg-green-900/20 border-l-green-500',
    };

    const typeColors = {
      pickup: 'bg-blue-50 dark:bg-blue-900/20 border-l-blue-500',
      delivery: 'bg-green-50 dark:bg-green-900/20 border-l-green-500',
    };

    const statusStyle = statusColors[event.status as keyof typeof statusColors];
    const typeStyle = typeColors[event.type as keyof typeof typeColors];
    const defaultStyle = 'bg-gray-50 dark:bg-gray-900/20 border-l-gray-400';

    return `${baseStyle} ${statusStyle || typeStyle || defaultStyle}`;
  };

  return (
    <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden'>
      {/* ヘッダー */}
      <div className='bg-gray-50 dark:bg-gray-700 px-4 py-3 border-b border-gray-200 dark:border-gray-600'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center'>
            <ClockIcon className='h-5 w-5 text-gray-500 dark:text-gray-400 mr-2' />
            <h3 className='text-sm font-medium text-gray-900 dark:text-white'>
              タイムスケジュール
            </h3>
          </div>
          <div className='text-xs text-gray-600 dark:text-gray-300'>
            {formatDateForDisplay(displayDate)}
          </div>
        </div>
      </div>

      {/* タイムスロット */}
      <div className='max-h-96 overflow-y-auto'>
        {timeSlots.map((slot, index) => {
          const events = getEventsForTime(slot.time);
          const isCurrentTime = index === currentTimeSlotIndex;

          return (
            <div
              key={slot.time}
              className={`relative border-b border-gray-100 dark:border-gray-700 ${
                isCurrentTime ? 'bg-red-50 dark:bg-red-900/10' : ''
              }`}
            >
              {/* 現在時刻のマーカー */}
              {isCurrentTime && (
                <div className='absolute left-0 top-0 w-full h-px bg-red-500 z-10' />
              )}

              <div className='flex'>
                {/* 時間表示 */}
                <div className='w-16 flex-shrink-0 py-2 px-2 text-xs text-gray-500 dark:text-gray-400 text-right'>
                  {slot.minute === 0 ? (
                    <span className='font-medium'>{slot.displayTime}</span>
                  ) : (
                    <span className='text-gray-400'>{slot.displayTime}</span>
                  )}
                </div>

                {/* イベント表示エリア */}
                <div className='flex-1 py-2 px-3 min-h-[3rem]'>
                  {events.length > 0 ? (
                    events.map(event => (
                      <div key={event.id} className={getCaseEventStyle(event)}>
                        <div className='flex items-start justify-between'>
                          <div className='flex-1'>
                            <div className='flex items-center mb-1'>
                              {event.type === 'pickup' ? (
                                <TruckIcon className='h-4 w-4 text-blue-600 dark:text-blue-400 mr-1' />
                              ) : (
                                <MapPinIcon className='h-4 w-4 text-green-600 dark:text-green-400 mr-1' />
                              )}
                              <span className='text-xs font-medium text-gray-900 dark:text-white'>
                                {event.type === 'pickup' ? '引き取り' : '搬入'}
                              </span>
                            </div>
                            <p className='text-xs text-gray-800 dark:text-gray-200 mb-1'>
                              {event.vehiclePlateNumber}
                            </p>
                            <p className='text-xs text-gray-600 dark:text-gray-400 truncate'>
                              {event.address}
                            </p>
                          </div>
                          <div className='text-xs text-gray-500 dark:text-gray-500'>
                            {event.duration}分
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className='h-12' /> // 空のスペース
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* フッター */}
      <div className='bg-gray-50 dark:bg-gray-700 px-4 py-2 text-xs text-gray-500 dark:text-gray-400'>
        <div className='flex items-center justify-between'>
          <span>総案件数: {cases.length}件</span>
          <span>総イベント数: {caseEvents.length}件</span>
        </div>
      </div>
    </div>
  );
};

export default DriverTimeSlotView;
